'use client'

import { useState } from 'react'

type DsrType =
  | 'access'
  | 'correction'
  | 'deletion'
  | 'portability'
  | 'confirmation'

const TYPE_OPTIONS: { value: DsrType; label: string; help: string }[] = [
  {
    value: 'access',
    label: 'Acesso aos meus dados',
    help: 'Receba uma copia de todos os dados pessoais que tratamos sobre voce (Art. 18 II).',
  },
  {
    value: 'correction',
    label: 'Correcao de dados',
    help: 'Solicite correcao de dados incompletos, inexatos ou desatualizados (Art. 18 III).',
  },
  {
    value: 'deletion',
    label: 'Exclusao / anonimizacao',
    help: 'Solicite exclusao ou anonimizacao dos seus dados (Art. 18 IV).',
  },
  {
    value: 'portability',
    label: 'Portabilidade',
    help: 'Receba seus dados em formato estruturado para portar a outro servico (Art. 18 V).',
  },
  {
    value: 'confirmation',
    label: 'Confirmacao de tratamento',
    help: 'Quero saber se voces tratam meus dados pessoais (Art. 18 I).',
  },
]

export default function LgpdRequestForm() {
  const [type, setType] = useState<DsrType>('access')
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErrorMsg(null)
    setSubmitting(true)

    try {
      const res = await fetch('/api/lgpd/requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          request_type: type,
          requestor_email: email,
          requestor_name: name || null,
          description: description || null,
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        setErrorMsg(json?.error ?? 'Falha ao enviar solicitacao.')
        return
      }
      setSubmitted(true)
    } catch (err) {
      setErrorMsg(
        err instanceof Error
          ? err.message
          : 'Erro de rede. Tente novamente em instantes.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="text-center py-6">
        <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-3">
          <svg
            className="w-6 h-6 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-stone-900 mb-1">
          Solicitacao recebida
        </h3>
        <p className="text-sm text-stone-600">
          Email de verificacao enviado para <strong>{email}</strong>. Clique no link
          do email para confirmar sua identidade. Apos isso nosso DPO inicia o
          atendimento em ate 15 dias.
        </p>
      </div>
    )
  }

  const selectedHelp = TYPE_OPTIONS.find((o) => o.value === type)?.help ?? ''

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-stone-700 mb-1">
          Tipo de pedido
        </label>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as DsrType)}
          className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <p className="text-xs text-stone-500 mt-1">{selectedHelp}</p>
      </div>

      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium text-stone-700 mb-1"
        >
          Email <span className="text-red-500">*</span>
        </label>
        <input
          id="email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="seu@email.com"
          className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <p className="text-xs text-stone-500 mt-1">
          Use o mesmo email que voce usa na plataforma. Enviaremos um link de
          verificacao para este endereco.
        </p>
      </div>

      <div>
        <label
          htmlFor="name"
          className="block text-sm font-medium text-stone-700 mb-1"
        >
          Nome completo (opcional)
        </label>
        <input
          id="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Como nos referirmos a voce"
          className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div>
        <label
          htmlFor="description"
          className="block text-sm font-medium text-stone-700 mb-1"
        >
          Descricao do pedido (opcional)
        </label>
        <textarea
          id="description"
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Detalhe sua solicitacao se necessario (ex: campos a corrigir, motivo da exclusao)"
          className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {errorMsg && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {errorMsg}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting || !email}
        className="w-full px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors"
      >
        {submitting ? 'Enviando...' : 'Enviar solicitacao'}
      </button>

      <p className="text-xs text-stone-500 text-center">
        Ao enviar, voce concorda com nossa{' '}
        <a href="/privacidade" className="underline hover:text-stone-700">
          Politica de Privacidade
        </a>
        .
      </p>
    </form>
  )
}
